import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { EntitySelect } from '@/components/EntitySelect';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { FormSection } from '@/components/ui/form-section';
import { InfoHint } from '@/components/ui/info-hint';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { ApiError } from '@/core/api';
import { resolveEmployeeLabel, searchEmployees } from '@/features/hrm/employees/select';
import { COUNTRIES } from '@/lib/countries';
import type { TaxProfile, TaxProfileInput } from '../types';

const schema = z.object({
  employee: z.string().min(1, 'Employee is required'),
  country: z
    .string()
    .trim()
    .max(2, 'Use the ISO 3166-1 alpha-2 code (e.g. US, GB).'),
  tax_id: z.string().max(64),
  tax_status: z.string().max(255),
  tax_status_local: z.string().max(255),
});

type FormValues = z.infer<typeof schema>;
const FIELD_KEYS = ['employee', 'country', 'tax_id', 'tax_status', 'tax_status_local'] as const;

interface TaxProfileFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  profile?: TaxProfile;
  onSubmit: (input: TaxProfileInput) => Promise<unknown>;
}

function toValues(profile?: TaxProfile): FormValues {
  return {
    employee: profile?.employee ? String(profile.employee) : '',
    country: profile?.country ?? '',
    // tax_id may come back masked ("••••") — don't pre-fill with the placeholder.
    tax_id: profile?.tax_id && !profile.tax_id.startsWith('•') ? profile.tax_id : '',
    tax_status: profile?.tax_status ?? '',
    tax_status_local: profile?.tax_status_local ?? '',
  };
}

export function TaxProfileFormDialog({ open, onOpenChange, profile, onSubmit }: TaxProfileFormDialogProps) {
  const form = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: toValues(profile) });

  const { reset } = form;
  useEffect(() => {
    if (open) reset(toValues(profile));
  }, [open, profile, reset]);

  async function handleSubmit(values: FormValues) {
    const input: TaxProfileInput = {
      employee: Number(values.employee),
      country: values.country.trim().toUpperCase(),
      tax_id: values.tax_id.trim(),
      tax_status: values.tax_status.trim(),
      tax_status_local: values.tax_status_local.trim(),
    };
    try {
      await onSubmit(input);
      onOpenChange(false);
    } catch (e) {
      const err = e as ApiError;
      if (err?.fieldErrors) {
        for (const [field, messages] of Object.entries(err.fieldErrors)) {
          const key = FIELD_KEYS.find((k) => k === field);
          form.setError(key ?? 'root', { message: messages.join(' ') });
        }
      }
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{profile ? 'Edit tax profile' : 'New tax profile'}</DialogTitle>
          <DialogDescription>
            Record-only — figures are entered, never computed.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
            <FormSection title="Profile">
              <div className="grid gap-4 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="employee"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Employee</FormLabel>
                      <FormControl>
                        <EntitySelect
                          queryKey="tax-employee-picker"
                          value={field.value}
                          onChange={field.onChange}
                          search={searchEmployees}
                          resolveLabel={resolveEmployeeLabel}
                          placeholder="Select an employee"
                          disabled={Boolean(profile)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="country"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Country</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select country" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {COUNTRIES.map((c) => (
                            <SelectItem key={c.code} value={c.code}>
                              {c.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </FormSection>

            <FormSection title="Tax ID">
              <FormField
                control={form.control}
                name="tax_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-1.5">
                      Tax ID
                      <InfoHint text="Masked for unauthorized roles." />
                    </FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="tax_status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-1.5">
                      Tax status
                      <InfoHint text="Printed verbatim on the staff invoice, e.g. “is not a VAT payer”." />
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="is not a VAT payer" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="tax_status_local"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tax status (local language)</FormLabel>
                    <FormControl>
                      <Input placeholder="не є платником ПДВ" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </FormSection>

            {form.formState.errors.root ? (
              <p className="text-destructive text-sm">{form.formState.errors.root.message}</p>
            ) : null}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={form.formState.isSubmitting}>
                {form.formState.isSubmitting ? 'Saving…' : profile ? 'Save changes' : 'Save tax profile'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
